import { uploadToSignedUrl } from './attachments';
import { ApiError } from './client';
// How long the uploads sharing one liveness clock may go without moving a
// single byte before an upload is declared dead. Generous on purpose: once a
// file's bytes are all handed to the network stack, progress events stop while
// the socket buffer drains, and on a very slow uplink that tail alone can run
// to tens of seconds.
export const UPLOAD_STALL_TIMEOUT_MS = 60_000;
const STALL_CHECK_INTERVAL_MS = 5_000;
export function createUploadLiveness() {
    return { lastProgressAt: Date.now() };
}
/**
 * PUTs `file` to a signed URL with no wall-clock deadline: a slow upload runs
 * for as long as bytes keep moving. It is aborted — and rejects with a
 * status-0 ApiError, the same shape as any other "couldn't reach storage"
 * failure — only once nothing sharing `liveness` has progressed for
 * UPLOAD_STALL_TIMEOUT_MS. Each call gets a full window of its own, so a retry
 * issued right after a stall isn't judged against the already-stale clock.
 *
 * A caller abort via `signal` is passed through unchanged (AbortError).
 * `onProgress`, when given, hears every byte count the browser reports for
 * this transfer (bytes sent so far, from zero for each call).
 */
export async function uploadWithStallGuard(uploadUrl, file, signal, liveness, onProgress) {
    const startedAt = Date.now();
    const stallController = new AbortController();
    let stalled = false;
    let lastLoaded = 0;
    const watchdog = setInterval(() => {
        const idleSince = Math.max(startedAt, liveness.lastProgressAt);
        if (Date.now() - idleSince >= UPLOAD_STALL_TIMEOUT_MS) {
            stalled = true;
            stallController.abort();
        }
    }, STALL_CHECK_INTERVAL_MS);
    try {
        await uploadToSignedUrl(uploadUrl, file, {
            signal: AbortSignal.any([signal, stallController.signal]),
            onProgress: (loaded) => {
                if (loaded > lastLoaded) {
                    lastLoaded = loaded;
                    liveness.lastProgressAt = Date.now();
                    onProgress?.(loaded);
                }
            },
        });
        // A file small enough to go out in one write may never emit a progress
        // event; landing is progress all the same.
        liveness.lastProgressAt = Date.now();
    }
    catch (error) {
        if (stalled && !signal.aborted)
            throw new ApiError(0, 'Upload stalled');
        throw error;
    }
    finally {
        clearInterval(watchdog);
    }
}
